/**
 * Pan and zoom for a map: drag to pan, Ctrl+wheel or pinch to zoom (a plain
 * wheel scrolls the page, as on the Trailblazers map), plus buttons.
 *
 * The plane (the image and its markers) is moved with three CSS custom
 * properties on it, `--map-k` (zoom) and `--map-tx` / `--map-ty` (offset), set
 * here without a React render per frame; the stylesheet turns them into the
 * transform and keeps markers the same size on screen at any zoom. The plane
 * fills the viewport at zoom 1 and can never be dragged off it.
 */
import { useCallback, useEffect, useRef } from 'react';
import { select } from 'd3-selection';
import { zoom, zoomIdentity } from 'd3-zoom';
/** How long a zoom to a rectangle, or back out, animates (ms). */
export const MAP_ZOOM_ANIMATION_MS = 450;
const ZOOM_STEP = 1.6;
/** The zoom that fits `rect` in the viewport, capped at `maxZoom`. */
export function fitZoom(rect, maxZoom) {
    return Math.max(1, Math.min(maxZoom, 100 / Math.max(rect.w, 0.01), 100 / Math.max(rect.h, 0.01)));
}
/** Keep the plane covering the viewport: no blank edge at any zoom. */
function constrain(transform, extent) {
    const [[x0, y0], [x1, y1]] = extent;
    const width = x1 - x0;
    const height = y1 - y0;
    const x = Math.min(0, Math.max(width - width * transform.k, transform.x));
    const y = Math.min(0, Math.max(height - height * transform.k, transform.y));
    return zoomIdentity.translate(x, y).scale(transform.k);
}
export function useMapZoom(viewportRef, planeRef, { enabled, maxZoom }) {
    const behaviour = useRef(null);
    const selection = useRef(null);
    const animation = useRef(null);
    useEffect(() => {
        const viewport = viewportRef.current;
        const plane = planeRef.current;
        if (!enabled || !viewport || !plane)
            return;
        const apply = (t) => {
            plane.style.setProperty('--map-k', String(t.k));
            plane.style.setProperty('--map-tx', `${t.x}px`);
            plane.style.setProperty('--map-ty', `${t.y}px`);
            viewport.classList.toggle('is-zoomed', t.k > 1.001);
        };
        const zoomer = zoom()
            .scaleExtent([1, maxZoom])
            .constrain(constrain)
            // A wheel zooms only with Ctrl (trackpad pinches arrive that way);
            // otherwise it scrolls the page. Any button but the main one is ignored.
            .filter((event) => event.type === 'wheel' ? event.ctrlKey : !event.button)
            .on('zoom', (event) => apply(event.transform));
        const sel = select(viewport);
        sel.call(zoomer);
        // Double-click zooms too far on a map with clickable markers.
        sel.on('dblclick.zoom', null);
        behaviour.current = zoomer;
        selection.current = sel;
        apply(zoomIdentity);
        return () => {
            sel.on('.zoom', null);
            behaviour.current = null;
            selection.current = null;
            if (animation.current)
                clearTimeout(animation.current);
        };
    }, [enabled, maxZoom, viewportRef, planeRef]);
    /** Run a programmatic move, animating it through the stylesheet's transition when asked. */
    const run = useCallback((move, animate) => {
        const zoomer = behaviour.current;
        const sel = selection.current;
        const viewport = viewportRef.current;
        if (!zoomer || !sel || !viewport)
            return Promise.resolve();
        if (animation.current)
            clearTimeout(animation.current);
        const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const animated = animate && !reduced;
        viewport.classList.toggle('is-animating', animated);
        move(zoomer, sel, viewport.clientWidth, viewport.clientHeight);
        if (!animated)
            return Promise.resolve();
        return new Promise((resolve) => {
            animation.current = setTimeout(() => {
                viewport.classList.remove('is-animating');
                animation.current = null;
                resolve();
            }, MAP_ZOOM_ANIMATION_MS);
        });
    }, [viewportRef]);
    const zoomToRect = useCallback((rect, animate = true) => {
        const k = fitZoom(rect, maxZoom);
        return run((zoomer, sel, width, height) => zoomer.transform(sel, zoomIdentity
            .translate(width / 2, height / 2)
            .scale(k)
            .translate(-((rect.x + rect.w / 2) / 100) * width, -((rect.y + rect.h / 2) / 100) * height)), animate);
    }, [run, maxZoom]);
    const reset = useCallback((animate = true) => run((zoomer, sel) => zoomer.transform(sel, zoomIdentity), animate), [run]);
    const zoomBy = useCallback((factor) => {
        void run((zoomer, sel) => zoomer.scaleBy(sel, factor), true);
    }, [run]);
    return {
        zoomIn: useCallback(() => zoomBy(ZOOM_STEP), [zoomBy]),
        zoomOut: useCallback(() => zoomBy(1 / ZOOM_STEP), [zoomBy]),
        reset,
        zoomToRect,
    };
}
